import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { paths } from "@/types/api";

type ManagementBody =
  paths["/api/v1/properties/{property_id}/management"]["put"]["requestBody"]["content"]["application/json"];
type MeterBody =
  paths["/api/v1/properties/{property_id}/meter-readings"]["post"]["requestBody"]["content"]["application/json"];

const mgmtKey = (id: string) => ["properties", id, "management"] as const;
const meterKey = (id: string) => ["properties", id, "meter-readings"] as const;

export function useManagement(propertyId: string) {
  return useQuery({
    queryKey: mgmtKey(propertyId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/properties/{property_id}/management",
        { params: { path: { property_id: propertyId } } },
      );
      if (error) throw new Error("Failed to load management record");
      return data ?? null;
    },
  });
}

export function useUpsertManagement(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    // Accept a partial record (form-built); the server fills defaults.
    mutationFn: async (body: Record<string, unknown>) => {
      const { data, error } = await apiClient.PUT(
        "/api/v1/properties/{property_id}/management",
        {
          params: { path: { property_id: propertyId } },
          body: body as ManagementBody,
        },
      );
      if (error || !data) throw new Error("Failed to save management record");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: mgmtKey(propertyId) }),
  });
}

export function useMeterReadings(propertyId: string) {
  return useQuery({
    queryKey: meterKey(propertyId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/properties/{property_id}/meter-readings",
        { params: { path: { property_id: propertyId } } },
      );
      if (error || !data) throw new Error("Failed to load meter readings");
      return data;
    },
  });
}

export function useAddMeterReading(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: MeterBody) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/properties/{property_id}/meter-readings",
        { params: { path: { property_id: propertyId } }, body },
      );
      if (error || !data) throw new Error("Failed to add meter reading");
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: meterKey(propertyId) }),
  });
}
